/**
 * cm-signup.js — emamaco first-party signup (Klaviyo signup replacement, P2).
 * Self-contained: styles injected, no dependencies. Three surfaces, one engine:
 *   1. Cart-drawer inline form — scv-drawer.js calls window.__cmSignup.inlineHtml()
 *      inside the Mum Club box; handlers here are DELEGATED so drawer re-renders
 *      never lose them.
 *   2. The Mum Club popup (Mark, 2026-10-10): "$100 gift card on us" for joining. She gives her email, joins
 *      (the plain line under the button is the consent: joining means emails), and a $100 card is made on the
 *      spot (api /giftcard/emamaco/ad/claim, campaign 'mumclub': the TikTok pages' card, same terms and
 *      switch), shown in the popup and put on her cart. ONLY for a visitor holding none of our gift cards
 *      (the cart attribute emamaco_gift_card, or cm_ad_card in this browser). Joining unlocks nothing:
 *      member prices come from the cart's Mum Club toggle for everyone, as before.
 *      The old step 2 (name / due date / SMS) still follows the cart-drawer signup.
 *   3. Sticky left-edge tab ("$100 gift card"), also only without a card; opens the popup. Its X hides it for that page
 *      only: it is back on the next page (a product swap counts), so she can find the card after closing the popup,
 *      and it never shows on the cart page or at checkout (Mark, 2026-10-14).
 *   The amount, the days and an on/off switch come from the helpdesk's Gift cards panel (mcOffer, 2026-10-10).
 * Popup triggers: ANY PAGE (Mark, 2026-10-10; was product pages only), 15s after the page loads OR exit-intent
 * (desktop). The TikTok card pages use their own layout and never load this script.
 * Caps: never after she joins through the card popup (LS_JOINED; an older sign-up does not count, Mark, 2026-10-14);
 * 3-day snooze after dismiss (Mark, 2026-10-10; was 30 days); once per page load;
 * never while the take box's recording screens are up (window.__cmTakesOpen, emamaco-packprice.liquid); never
 * on a page where the take box's gift card sheet showed (one popup per page, window.__cmPopupShown); never
 * again in a visit where she tapped the take box (window.__cmTakesEngaged / cm_takes_engaged).
 * PRICING IS A NO-OP: the form presents as the unlock; pricing applies as today.
 * Events: CVR section_viewed/email_capture (vid/sid-less, endpoint tolerates) +
 * Meta Pixel Lead on success (replaces the old Klaviyo klaviyoForms listener).
 */
(function () {
  'use strict';
  if (window.__cmSignup) return;

  var API = 'https://api.convomesh.com/collect/emamaco-signup';
  var CVR = 'https://api.convomesh.com/collect/emamaco-cvr';
  var LS_DONE = 'cm_mc_signup_done';
  // Joined through the gift card popup. Only this ends the card offer (popup, tab, badges, the pack builder's button):
  // an earlier sign-up (LS_DONE: the popup before it gave a card, the cart's email box) still gets offered the card
  // (Mark, 2026-10-14: "Let earlier sign-ups see it. Show the offer to anyone without a card, even if they signed up
  // before. Only joining the gift card version would hide it").
  var LS_JOINED = 'cm_mc_card_joined';
  var LS_SNOOZE = 'cm_mc_signup_snooze';
  var SNOOZE_MS = 3 * 24 * 3600 * 1000;
  var CONSENT_TEXT_INLINE = 'Email me offers & drops';
  // The popup has no checkbox (Mark, 2026-10-10): this line sits under the join button, and joining is the consent.
  var CONSENT_TEXT_MODAL = 'Joining means we\'ll email you offers. Unsubscribe anytime.';
  var CLAIM = 'https://api.convomesh.com/giftcard/emamaco/ad/claim';
  // The popup's photo, and the white logo the TikTok card pages use (beside this script in the theme's assets).
  var HERO = 'https://cdn.shopify.com/s/files/1/0117/6305/6699/files/emama-black-long-line-nursing-crop-maternity-top-woman-athletic-wear-661.webp?v=1750677694&width=900';
  var SCRIPT_SRC = (document.currentScript && document.currentScript.src) || '';
  var LOGO = SCRIPT_SRC ? SCRIPT_SRC.replace(/cm-signup\.js(\?.*)?$/, 'cm-ad-logo-white.png') : '';
  var CONSENT_TEXT_SMS = 'text me sales, drops and order updates so I never miss out';
  // Universal SMS disclosure written to the strictest standard we sell into (US TCPA/CTIA);
  // also satisfies AU Spam Act / NZ UEMA / UK PECR, so one text covers every geo.
  var SMS_DISCLAIMER = 'By opting in to texts you agree to receive recurring automated marketing messages from Emamaco at the number provided. Consent is not a condition of purchase. Msg & data rates may apply, message frequency varies. Reply STOP to opt out, HELP for help.';

  function ls(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function done() { return ls(LS_DONE) === '1'; }
  function snoozed() { var t = parseInt(ls(LS_SNOOZE) || '0', 10); return t > 0 && (Date.now() - t) < SNOOZE_MS; }

  // SHE ALREADY HOLDS ONE OF OUR GIFT CARDS: no popup and no tab (Mark, 2026-10-10: "only open the mum club popup
  // for people who havnt got a live gift card from the physios/tiktok/etc"). The same two signals the product
  // page's take box reads (emamaco-packprice.liquid holdsCard): the cart attribute every card page and link sets,
  // and the card those pages keep in this browser until it expires.
  // Only a LIVE card loaded in this visit counts (Mark, 2026-10-20: "if i join and get a gift card it shouldnt stop it
  // appearing! it should only stop appearing if i have a gift card loaded in my session"): the theme's cmHeldCard
  // (layout/theme.liquid) says so when the cart carries the card, this browser keeps the same card, it hasn't expired
  // and its balance (checked by cm-ad-card.js once a visit) isn't used up. A spent or expired card, or one only on
  // the cart, no longer hides the offer, and joining before doesn't either.
  var CARD_ON = false;
  var lastCart = null;
  function liveCard(cart) {
    if (typeof window.cmHeldCard === 'function') return !!window.cmHeldCard(cart);
    return !!(cart && cart.attributes && cart.attributes.emamaco_gift_card);
  }
  function holdsCard() { return CARD_ON; }
  // THE OFFER (Mark, 2026-10-10): the helpdesk's Gift cards panel sets the Mum Club popup's amount, days and switch;
  // the theme hands them over as window.__cmGiftOffers (shop metafield cm_ad.gift_offers, layout/theme.liquid).
  // Nothing set yet: $100 for 3 days, on. Off: no popup and no tab at all.
  function mcOffer() {
    var o = (window.__cmGiftOffers && window.__cmGiftOffers.mumclub) || null;
    return {
      on: !o || o.on !== false,
      amount: o && Number(o.amount) > 0 ? Math.round(Number(o.amount)) : 100,
      days: o && Number(o.days) > 0 ? Math.round(Number(o.days)) : 3
    };
  }
  // The card's sign: '$' unless this website's cards are in euros (Sweden; the runtime puts it in the offers).
  function cardSym() { return (window.__cmGiftOffers && window.__cmGiftOffers.symbol) || '$'; }
  function amtText() { return cardSym() + mcOffer().amount; }
  function daysText() { var d = mcOffer().days; return d === 1 ? '1 day' : d + ' days'; }
  // No popup and no tab: she holds a card, or the offer is switched off.
  function noOffer() { return holdsCard() || !mcOffer().on; }
  var cardChecked = fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (c) { lastCart = c; CARD_ON = liveCard(c); })
    .catch(function () {});
  window.addEventListener('emamaco:gift-card-on', function () { CARD_ON = true; removeTab(); normalizeBadges(); });
  // cm-ad-card.js has checked the card's live balance: a card that is used up or gone gives the offer back.
  window.addEventListener('cm:card-checked', function () {
    cardChecked.then(function () {
      CARD_ON = liveCard(lastCart);
      if (CARD_ON) removeTab(); else mountTab();
      normalizeBadges();
    });
  });

  function cvr(eventType, props) {
    try {
      var body = {
        event_type: eventType, ts: new Date().toISOString(),
        shop_host: location.host, page_type: 'signup', page_path: location.pathname,
        properties: props || {}
      };
      fetch(CVR, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true }).catch(function () {});
    } catch (e) {}
  }
  function pixelLead(surface) {
    try { if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_name: 'cm_signup_' + surface, source: 'convomesh' }); } catch (e) {}
  }

  function submit(payload) {
    return fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true })
      .then(function (r) { return r.json(); });
  }
  // ── Inferred-lifestage context (belt-and-suspenders lifestage stamp) ─────────
  // We give the signup the one real signal it otherwise throws away: what's in the
  // cart (matches the slideout's own inferLifestage), or which product's Mum Club
  // button they clicked. The backend stamps it as an INFERRED source — never over a
  // manual/declared stage. No cart/theme coupling: we read /cart.js + the URL here.
  // The cart the signup was given in. Sent with the cart-inline signup so the backend can publish it as
  // a cart event WITH the person attached (Shopify's own cart webhooks never carry the email). Only the
  // fields that event needs; the backend validates and caps them again.
  function compactCart(c) {
    return {
      token: c.token, currency: c.currency,
      items: (c.items || []).slice(0, 50).map(function (it) {
        return {
          key: it.key, product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity,
          price: it.price, product_title: it.product_title, variant_title: it.variant_title,
          sku: it.sku, vendor: it.vendor, handle: it.handle, image: it.image
        };
      })
    };
  }
  function cartInfer() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || !c.items || !c.items.length) return null;
        var hasMat = false, hasPP = false;
        c.items.forEach(function (it) {
          var t = String(it.product_title || it.title || '').toLowerCase();
          if (t.indexOf('maternity') >= 0 || t.indexOf('mat ') >= 0) hasMat = true;
          if (t.indexOf('recovery') >= 0 || t.indexOf('nursing') >= 0) hasPP = true;
        });
        return {
          lifestage: hasMat ? 'pregnant' : (hasPP ? 'postpartum' : 'mum'), // mirrors cart-set-view inferLifestage
          cart: compactCart(c)
        };
      })
      .catch(function () { return null; });
  }
  function pdpHandle() { var m = location.pathname.match(/\/products\/([a-z0-9-]+)/i); return m ? m[1] : null; }
  function inferCtx(surface) {
    if (surface === 'cart-inline') return cartInfer().then(function (r) { return r ? { inferredLifestage: r.lifestage, cart: r.cart } : {}; });
    var h = pdpHandle();                                        // Mum Club on a PDP → the product they're viewing
    return Promise.resolve(h ? { productHandle: h } : {});
  }

  function bank(email, surface) {
    return inferCtx(surface).then(function (ctx) {
      var payload = { email: email, consentEmail: true, surface: surface, consentText: surface === 'cart-inline' ? CONSENT_TEXT_INLINE : CONSENT_TEXT_MODAL };
      if (ctx.inferredLifestage) payload.inferredLifestage = ctx.inferredLifestage;
      if (ctx.productHandle) payload.productHandle = ctx.productHandle;
      if (ctx.cart) payload.cart = ctx.cart;
      return submit(payload);
    })
      .then(function (d) {
        if (d && d.ok) {
          lsSet(LS_DONE, '1');
          window.__cmSigEmail = email;
          cvr('email_capture', { surface: surface });
          pixelLead(surface);
          normalizeBadges();
          return true;
        }
        return false;
      });
  }

  // ── styles ─────────────────────────────────────────────────────────────────
  var css = ''
    + '.cm-sig-inline{margin-top:8px;}'
    + '.cm-sig-row{display:flex;gap:6px;margin-top:6px;}'
    + '.cm-sig-row input[type=email]{flex:1;min-width:0;padding:9px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;color:#1f2937;background:#fff;}'
    + '.cm-sig-apply{padding:9px 14px;border:0;border-radius:8px;background:#1f2937;color:#fff;font-size:12px;font-weight:700;letter-spacing:.4px;cursor:pointer;}'
    + '.cm-sig-apply[disabled]{opacity:.45;cursor:default;}'
    + '.cm-sig-consent{display:flex;align-items:center;gap:6px;margin-top:6px;font-size:11.5px;color:#6b7280;}'
    + '.cm-sig-consent input{margin:0;}'
    + '.cm-sig-ok{margin-top:8px;font-size:12.5px;font-weight:700;color:#15803d;}'
    + '.cm-sig-err{margin-top:6px;font-size:11.5px;color:#b91c1c;}'
    + '.cm-tab{position:fixed;left:0;top:50%;transform:translateY(-50%);z-index:9990;display:flex;flex-direction:column;align-items:center;background:#1f2937;color:#fff;border-radius:0 8px 8px 0;cursor:pointer;border:0;padding:0 0 12px;}'
    + '.cm-tab-x{background:none;border:0;color:#9ca3af;font-size:15px;line-height:1;padding:8px 10px 6px;cursor:pointer;display:block;}'
    + '.cm-tab-x:hover{color:#fff;}'
    + '.cm-tab-label{writing-mode:vertical-rl;text-orientation:mixed;font-size:12px;font-weight:700;letter-spacing:.5px;line-height:1;padding:2px 8px 0;}'
    + '.cm-modal-ov{position:fixed;inset:0;background:rgba(17,24,39,.55);z-index:100010;display:flex;align-items:center;justify-content:center;padding:16px;}'
    + '.cm-modal{background:#fff;border-radius:14px;max-width:400px;width:100%;padding:22px 20px;position:relative;font-family:inherit;box-shadow:0 18px 50px rgba(0,0,0,.28);}'
    + '.cm-modal h3{margin:0 0 6px;font-size:19px;line-height:1.25;color:#1f2937;}'
    + '.cm-modal p{margin:0 0 12px;font-size:13.5px;line-height:1.5;color:#4b5563;}'
    + '.cm-modal .cm-x{position:absolute;top:8px;right:10px;background:none;border:0;font-size:20px;color:#9ca3af;cursor:pointer;padding:4px;}'
    + '.cm-modal input[type=email],.cm-modal input[type=text],.cm-modal input[type=tel],.cm-modal input[type=date]{width:100%;box-sizing:border-box;padding:10px 11px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;color:#1f2937;background:#fff;margin-top:8px;}'
    + '.cm-modal .cm-go{width:100%;margin-top:10px;padding:12px;border:0;border-radius:8px;background:#1f2937;color:#fff;font-size:14px;font-weight:700;cursor:pointer;}'
    + '.cm-modal .cm-go[disabled]{opacity:.45;cursor:default;}'
    + '.cm-modal .cm-skip{display:block;margin:10px auto 0;background:none;border:0;font-size:12px;color:#9ca3af;text-decoration:underline;cursor:pointer;}'
    + '.cm-modal label.cm-ck{display:flex;align-items:flex-start;gap:7px;margin-top:9px;font-size:12px;color:#6b7280;line-height:1.4;}'
    + '.cm-modal label.cm-ck input{margin:2px 0 0;}'
    + '.cm-seg{display:flex;gap:6px;margin-top:8px;}'
    + '.cm-seg-btn,.cm-age-btn{flex:1;padding:9px 6px;border:1px solid #d1d5db;border-radius:8px;background:#fff;font-size:12.5px;font-weight:600;color:#4b5563;cursor:pointer;text-align:center;}'
    + '.cm-seg-btn.selected,.cm-age-btn.selected{background:#1f2937;color:#fff;border-color:#1f2937;}'
    + '.cm-age-row{display:flex;gap:6px;margin-top:8px;}'
    + '.cm-stage-hint{font-size:11.5px;color:#9ca3af;margin:6px 0 0;}'
    // ── the Mum Club popup: the TikTok card pages' look (Anton, cream, coral, square corners, the black card) ──
    + '@font-face{font-family:"cm-mc-anton";font-weight:400;font-style:normal;font-display:swap;src:url("/cdn/fonts/anton/anton_n4.2ba6159dc1abf4f060e423ec24adc22041130b7f.woff2") format("woff2"),url("/cdn/fonts/anton/anton_n4.4dfcf4cc9b33342f25e44f2b47f32840ec097e13.woff") format("woff");}'
    + '.cm-mc-ov{position:fixed;inset:0;z-index:100010;background:rgba(20,18,16,.62);overflow-y:auto;-webkit-overflow-scrolling:touch;display:flex;padding:12px;box-sizing:border-box;}'
    + '.cm-mc{margin:auto;width:100%;max-width:400px;background:#FBF7F2;color:#1d1b19;box-shadow:0 18px 50px rgba(0,0,0,.35);position:relative;text-align:left;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;letter-spacing:normal;font-variant:normal;text-transform:none;}'
    + '.cm-mc *{box-sizing:border-box;}'
    + '.cm-mc-hero{position:relative;height:300px;overflow:hidden;background:#2a2622;color:#fff;}'
    + '.cm-mc-hero.cm-mc-short{height:170px;}'
    + '.cm-mc-photo{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:62% 18%;}'
    + '.cm-mc-shade{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.8),rgba(0,0,0,.05) 58%,rgba(0,0,0,.35));}'
    + '.cm-mc-logo{position:absolute;top:16px;left:50%;transform:translateX(-50%);height:17px;width:auto;}'
    + '.cm-mc-x{position:absolute;top:4px;right:4px;width:44px;height:44px;border:0;background:transparent;color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;z-index:2;}'
    + '.cm-mc-x svg{width:18px;height:18px;display:block;}'
    + '.cm-mc-foot{position:absolute;left:18px;right:18px;bottom:16px;display:flex;flex-direction:column;gap:8px;}'
    + '.cm-mc-sticker{align-self:flex-start;background:#fff;color:#1d1b19;font-size:11px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;padding:6px 9px;line-height:1;}'
    + '.cm-mc-big{margin:0;display:flex;align-items:flex-end;gap:12px;font-family:"cm-mc-anton",Anton,Impact,"Arial Narrow",sans-serif;font-weight:400;line-height:.86;text-transform:uppercase;color:#fff;letter-spacing:normal;}'
    + '.cm-mc-amt{font-size:88px;}'
    + '.cm-mc-stack{display:flex;flex-direction:column;gap:5px;padding-bottom:1px;}'
    + '.cm-mc-gc{font-size:30px;color:#fff;}'
    + '.cm-mc-onus{font-size:40px;color:#E8846C;}'
    + '.cm-mc-sub{margin:0;font-size:14px;line-height:1.4;font-weight:600;color:#fff;}'
    + '.cm-mc-title{position:absolute;left:18px;right:60px;bottom:14px;margin:0;font-family:"cm-mc-anton",Anton,Impact,"Arial Narrow",sans-serif;font-weight:400;font-size:38px;line-height:1.02;text-transform:uppercase;color:#fff;letter-spacing:normal;}'
    + '.cm-mc-title em{font-style:normal;color:#E8846C;}'
    + '.cm-mc-body{padding:16px 18px 6px;display:flex;flex-direction:column;}'
    + '.cm-mc-h3{display:none;margin:0 0 6px;font-family:"cm-mc-anton",Anton,Impact,"Arial Narrow",sans-serif;font-weight:400;font-size:40px;line-height:1;text-transform:uppercase;color:#1d1b19;letter-spacing:normal;}'
    + '.cm-mc-lbl{margin:0 0 10px;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#6b6058;}'
    + '.cm-mc-lbl .cm-mc-wide{display:none;}'
    + '.cm-mc-list{margin:0 0 14px;padding:0;list-style:none;display:flex;flex-direction:column;gap:7px;font-size:14px;line-height:1.35;color:#1d1b19;}'
    + '.cm-mc-list li{display:flex;gap:9px;align-items:flex-start;margin:0;padding:0;list-style:none;}'
    + '.cm-mc-list li:before{content:none;}'
    + '.cm-mc-list svg{flex:0 0 auto;margin-top:2px;width:16px;height:16px;}'
    + '.cm-mc-in{display:block;width:100%;border:1.5px solid #d8cfc6;background:#fff;padding:13px 12px;font-size:16px;font-family:inherit;color:#1d1b19;border-radius:0;margin:0;-webkit-appearance:none;appearance:none;box-shadow:none;min-height:0;}'
    + '.cm-mc-in:focus{outline:none;border-color:#1d1b19;}'
    + '.cm-mc-btn{display:block;width:100%;text-align:center;padding:15px 12px;font-weight:800;font-size:15px;line-height:1.2;font-family:inherit;letter-spacing:.02em;margin:8px 0 0;border:0;border-radius:0;cursor:pointer;text-decoration:none;-webkit-appearance:none;text-transform:none;min-height:0;}'
    + '.cm-mc-btn[disabled]{opacity:.65;cursor:default;}'
    + '.cm-mc-coral{background:#E8846C;color:#1d1b19;}'
    + '.cm-mc-ghost{background:transparent;color:#1d1b19;border:1.5px solid #1d1b19;}'
    + '.cm-mc-row{display:flex;gap:6px;margin-top:8px;}'
    + '.cm-mc-row .cm-mc-btn{flex:1;margin:0;font-size:13px;padding:13px 8px;}'
    + '.cm-mc-plain{margin:10px 0 0;font-size:12.5px;line-height:1.45;color:#4f463f;}'
    + '.cm-mc-fine{margin:6px 0 0;font-size:11.5px;line-height:1.45;color:#6b6058;}'
    + '.cm-mc-err{margin:8px 0 0;font-size:13px;font-weight:600;color:#b42318;}'
    + '.cm-mc-no{align-self:center;margin:4px 0 0;min-height:44px;padding:0 16px;border:0;background:transparent;color:#1d1b19;font-size:13px;font-weight:600;text-decoration:underline;text-underline-offset:3px;cursor:pointer;font-family:inherit;}'
    + '.cm-mc-card{position:relative;background:#1d1b19;color:#fff;padding:16px 16px 18px;display:flex;flex-direction:column;gap:10px;}'
    + '.cm-mc-card img{height:13px;width:auto;align-self:flex-start;}'
    + '.cm-mc-exp{position:absolute;right:14px;top:14px;font-size:11px;font-weight:600;color:#d9d2cb;}'
    + '.cm-mc-for{font-size:12px;font-weight:700;letter-spacing:.04em;}'
    + '.cm-mc-camt{font-family:"cm-mc-anton",Anton,Impact,"Arial Narrow",sans-serif;font-size:68px;line-height:.9;}'
    + '.cm-mc-code{font-size:16px;font-weight:800;letter-spacing:.14em;font-variant-numeric:tabular-nums;word-spacing:.1em;}'
    + '.cm-mc-on{margin:12px 0 4px;font-size:13.5px;line-height:1.45;color:#3f3a35;}'
    + '.cm-mc-on b{color:#1d1b19;}'
    + '.cm-mc-wait{display:inline-block;width:14px;height:14px;border:2px solid rgba(29,27,25,.35);border-top-color:#1d1b19;border-radius:50%;vertical-align:-2px;margin-right:8px;animation:cmmcspin .7s linear infinite;}'
    + '@keyframes cmmcspin{to{transform:rotate(360deg)}}'
    + '@media (prefers-reduced-motion:reduce){.cm-mc-wait{animation:none;}}'
    // Desktop: two columns, the photo on the left and the form on the right.
    + '@media (min-width:760px){'
    +   '.cm-mc{max-width:880px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));box-shadow:0 24px 70px rgba(0,0,0,.4);}'
    +   '.cm-mc-hero,.cm-mc-hero.cm-mc-short{height:auto;min-height:560px;}'
    +   '.cm-mc-photo{object-position:64% 30%;}'
    +   '.cm-mc-logo{left:28px;top:24px;transform:none;height:19px;}'
    +   '.cm-mc-foot{left:28px;right:28px;bottom:26px;gap:10px;}'
    +   '.cm-mc-amt{font-size:104px;}.cm-mc-gc{font-size:36px;}.cm-mc-onus{font-size:48px;}.cm-mc-sub{font-size:15px;}'
    +   '.cm-mc-title{left:28px;right:28px;bottom:26px;font-size:48px;}'
    +   '.cm-mc-body{padding:52px 40px 28px;justify-content:center;}'
    +   '.cm-mc-x{top:8px;right:8px;color:#1d1b19;}'
    +   '.cm-mc-h3{display:block;}'
    +   '.cm-mc-lbl{margin-bottom:18px;}.cm-mc-lbl .cm-mc-wide{display:inline;}.cm-mc-lbl .cm-mc-narrow{display:none;}'
    +   '.cm-mc-list{gap:9px;font-size:15px;margin-bottom:20px;}'
    +   '.cm-mc-no{align-self:flex-start;margin-left:-4px;padding:0 4px;}'
    + '}';
  var st = document.createElement('style'); st.textContent = css; document.head.appendChild(st);

  // ── 1. cart-drawer inline (markup provided to scv-drawer.js) ───────────────
  function inlineHtml() {
    if (done()) return '';
    var v = window.__cmSigEmailVal ? String(window.__cmSigEmailVal).replace(/"/g, '&quot;') : '';
    return '<div class="cm-sig-inline" data-cm-sig-shown="1">'
      + '<div class="cm-sig-row">'
      + '<input type="email" id="cm-sig-email" placeholder="enter email for mum club prices" autocomplete="email" value="' + v + '">'
      + '<button type="button" class="cm-sig-apply" id="cm-sig-apply">APPLY</button>'
      + '</div>'
      + '<label class="cm-sig-consent"><input type="checkbox" id="cm-sig-consent" checked> ' + CONSENT_TEXT_INLINE + '</label>'
      + '<div class="cm-sig-err" id="cm-sig-err" style="display:none;"></div>'
      + '</div>';
  }
  function active() { return !done(); }

  var inlineShownFired = false;
  document.addEventListener('input', function (e) {
    if (e.target && e.target.id === 'cm-sig-email') window.__cmSigEmailVal = e.target.value;
  });
  document.addEventListener('change', function (e) {
    if (e.target && e.target.id === 'cm-sig-consent') {
      var btn = document.getElementById('cm-sig-apply');
      if (btn) btn.disabled = !e.target.checked;
    }
  });
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.id === 'cm-sig-apply') {
      var emailEl = document.getElementById('cm-sig-email');
      var ckEl = document.getElementById('cm-sig-consent');
      var errEl = document.getElementById('cm-sig-err');
      var email = (emailEl && emailEl.value || '').trim();
      if (!ckEl || !ckEl.checked) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        if (errEl) { errEl.textContent = 'Please enter a valid email.'; errEl.style.display = 'block'; }
        return;
      }
      t.disabled = true; t.textContent = '...';
      bank(email, 'cart-inline').then(function (ok) {
        var wrap = t.closest('.cm-sig-inline');
        if (ok && wrap) {
          wrap.innerHTML = '<div class="cm-sig-ok">Mum Club unlocked for this order. Check your inbox.</div>';
          setTimeout(function () { openModal(true); }, 600); // step 2 (optional details)
        } else {
          t.disabled = false; t.textContent = 'APPLY';
          if (errEl) { errEl.textContent = 'Something went wrong - please try again.'; errEl.style.display = 'block'; }
        }
      }).catch(function () { t.disabled = false; t.textContent = 'APPLY'; });
    }
  });
  // ── SPA PDP price badge (.cm-mc-badge, rendered by emamaco-packprice.liquid) ──
  // Unsigned: "unlock mum club prices >" button → modal. Signed up: normalize back
  // to the plain "mum club price" badge. SPA product swaps re-render the fragment,
  // so normalization also runs from the MutationObserver below.
  function normalizeBadges() {
    if (!noOffer()) return;
    var els = document.querySelectorAll('.cm-mc-badge');
    for (var i = 0; i < els.length; i++) {
      if (els[i].textContent !== 'mum club price') els[i].textContent = 'mum club price';
      els[i].style.cursor = 'default';
    }
  }
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('.cm-mc-badge') : null;
    if (b && !noOffer()) { e.preventDefault(); openModal(false, 'pdp-badge'); }
  });

  // shown event: fire once when the drawer form first appears; badges normalize on SPA re-renders
  var mo = new MutationObserver(function () {
    if (!inlineShownFired && document.querySelector('[data-cm-sig-shown]')) {
      inlineShownFired = true;
      cvr('section_viewed', { section: 'cm-signup', surface: 'cart-inline' });
    }
    normalizeBadges();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // ── 2. modal (popup + sticky tab target; also step 2 after inline) ─────────
  var modalEl = null;
  function closeModal(dismissed) {
    if (modalEl) { modalEl.remove(); modalEl = null; }
    if (dismissed) lsSet(LS_SNOOZE, String(Date.now()));
  }
  function step2Html() {
    return '<h3>Tell us where you&rsquo;re at</h3>'
      + '<p>So we pick the right things for you &amp; bub.</p>'
      + '<input type="text" id="cm-m-name" placeholder="First name" autocomplete="given-name">'
      + '<div class="cm-seg" id="cm-stage">'
      +   '<button type="button" class="cm-seg-btn" data-stage="expecting">I&rsquo;m expecting</button>'
      +   '<button type="button" class="cm-seg-btn" data-stage="born">Baby is here</button>'
      + '</div>'
      + '<div id="cm-stage-expecting" style="display:none;">'
      +   '<input type="date" id="cm-m-due" aria-label="Due date">'
      +   '<p class="cm-stage-hint">Your due date unlocks week-by-week picks &amp; the pregnancy tracker.</p>'
      + '</div>'
      + '<div id="cm-stage-born" style="display:none;">'
      +   '<div class="cm-age-row">'
      +     '<button type="button" class="cm-age-btn" data-age="under-1">Under 1</button>'
      +     '<button type="button" class="cm-age-btn" data-age="1-2">1&ndash;2 yrs</button>'
      +     '<button type="button" class="cm-age-btn" data-age="over-2">2+ yrs</button>'
      +   '</div>'
      +   '<p class="cm-stage-hint">How old is your little one?</p>'
      + '</div>'
      + '<input type="tel" id="cm-m-phone" placeholder="Mobile (optional)" autocomplete="tel">'
      + '<label class="cm-ck"><input type="checkbox" id="cm-m-sms"> ' + CONSENT_TEXT_SMS + '</label>'
      + '<button type="button" class="cm-go" id="cm-m-save">Save</button>'
      + '<p style="font-size:10.5px;color:#9ca3af;line-height:1.45;margin:8px 0 0;">' + SMS_DISCLAIMER + '</p>'
      + '<button type="button" class="cm-skip" id="cm-m-skip">No thanks</button>';
  }
  // ── the Mum Club popup's screens ──────────────────────────────────────────────────────────────
  var CHECK = '<svg viewBox="0 0 16 16" fill="none" stroke="#1d1b19" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5"></path></svg>';
  var XBTN = '<button type="button" class="cm-mc-x" aria-label="Close"><svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M3 3l12 12M15 3L3 15"></path></svg></button>';
  function escH(v) { return String(v == null ? '' : v).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); }
  function wide() { try { return window.matchMedia('(min-width: 760px)').matches; } catch (e) { return false; } }
  function money(c) { var d = Number(c) / 100; return cardSym() + (d % 1 === 0 ? String(d) : d.toFixed(2)); }
  function fmtCode(c) { return String(c || '').toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/(.{4})(?=.)/g, '$1 '); }
  // Parsed by hand: new Date('YYYY-MM-DD') is UTC midnight and prints as the day before in Australia.
  function ymdDate(iso) { var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '')); return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null; }
  function dayText(iso, opts) { var d = ymdDate(iso); if (!d) return ''; try { return d.toLocaleDateString('en-AU', opts).replace(/,/g, ''); } catch (e) { return String(iso); } }
  function logoImg(cls) { return LOGO ? '<img' + (cls ? ' class="' + cls + '"' : '') + ' src="' + escH(LOGO) + '" alt="emamaco" width="480" height="116">' : ''; }
  function heroHtml(inner, short) {
    return '<div class="cm-mc-hero' + (short ? ' cm-mc-short' : '') + '">'
      + '<img class="cm-mc-photo" src="' + escH(HERO) + '" alt="A mum in emamaco activewear" width="900" height="900">'
      + '<div class="cm-mc-shade"></div>' + logoImg('cm-mc-logo') + inner + '</div>';
  }
  function joinHtml(surface) {
    return XBTN + heroHtml(
        '<div class="cm-mc-foot">'
      +   '<span class="cm-mc-sticker">Mum Club &middot; free to join</span>'
      +   '<h2 class="cm-mc-big"><span class="cm-mc-amt">' + amtText() + '</span><span class="cm-mc-stack"><span class="cm-mc-gc">Gift card</span><span class="cm-mc-onus">On us</span></span></h2>'
      +   '<p class="cm-mc-sub">Join Mum Club and it&rsquo;s yours to use or gift.</p>'
      + '</div>')
      + '<div class="cm-mc-body">'
      +   '<h3 class="cm-mc-h3">Join Mum Club</h3>'
      +   '<p class="cm-mc-lbl"><span class="cm-mc-narrow">Join Mum Club. It&rsquo;s free.</span><span class="cm-mc-wide">It&rsquo;s free. Just your email.</span></p>'
      +   '<ul class="cm-mc-list">'
      +     '<li>' + CHECK + '<span>Mum Club prices on everything</span></li>'
      +     '<li>' + CHECK + '<span>A ' + amtText() + ' gift card to use or gift</span></li>'
      +     '<li>' + CHECK + '<span>Offers from our partner women&rsquo;s health professionals</span></li>'
      +   '</ul>'
      +   '<label for="cm-mc-email" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);">Email</label>'
      +   '<input class="cm-mc-in" type="email" id="cm-mc-email" placeholder="' + (wide() ? 'Your email' : 'Just your email') + '" autocomplete="email" inputmode="email" autocapitalize="off" spellcheck="false" enterkeyhint="go">'
      +   '<button type="button" class="cm-mc-btn cm-mc-coral" id="cm-mc-go" data-surface="' + escH(surface) + '">Join and get my ' + amtText() + '</button>'
      +   '<p class="cm-mc-err" id="cm-mc-err" role="alert" style="display:none;"></p>'
      +   '<p class="cm-mc-plain">Joining means we&rsquo;ll email you offers. Unsubscribe anytime.</p>'
      +   '<p class="cm-mc-fine">Gift card valid ' + daysText() + '. Use it or gift it. One gift card per order. Not exchangeable for cash.</p>'
      +   '<button type="button" class="cm-mc-no" id="cm-mc-no">No thanks</button>'
      + '</div>';
  }
  function cardHtml(card, line) {
    return '<div class="cm-mc-card">' + logoImg('')
      + '<span class="cm-mc-exp">Valid until ' + escH(dayText(card.expires_on, { weekday: 'short', day: 'numeric', month: 'short' })) + '</span>'
      + '<span class="cm-mc-for">' + escH(line) + '</span>'
      + '<span class="cm-mc-camt">' + escH(money(card.amount_cents)) + '</span>'
      + '<span class="cm-mc-code">' + escH(fmtCode(card.code)) + '</span>'
      + '</div>';
  }
  function fineHtml(card) {
    return '<p class="cm-mc-fine" style="margin:12px 0 8px;">Valid until the end of ' + escH(dayText(card.expires_on, { weekday: 'long', day: 'numeric', month: 'long' }))
      + '. One gift card per order. Not exchangeable for cash.</p>';
  }
  function joinedHtml(card) {
    return XBTN + heroHtml('<h2 class="cm-mc-title">Welcome to <em>Mum Club</em></h2>', true)
      + '<div class="cm-mc-body">' + cardHtml(card, 'Mum Club gift card')
      +   '<p class="cm-mc-on">' + (card.onCart ? '<b>It&rsquo;s on your cart</b> and comes off at checkout.' : '<b>Pop this code in at checkout.</b>')
      +     (card.emailed ? ' We&rsquo;ve emailed it to you too.' : '') + '</p>'
      +   '<button type="button" class="cm-mc-btn cm-mc-coral" id="cm-mc-shop">Keep shopping</button>'
      +   '<div class="cm-mc-row">'
      +     '<button type="button" class="cm-mc-btn cm-mc-ghost" id="cm-mc-gift">Gift it to a friend</button>'
      +     '<button type="button" class="cm-mc-btn cm-mc-ghost" id="cm-mc-copy">Copy code</button>'
      +   '</div>'
      +   fineHtml(card)
      + '</div>';
  }
  // Giving it away: off her cart (the card pages do the same), and the card's own link to send. Whoever opens the
  // link gets it on their cart.
  function giftHtml(card) {
    var canShare = !!(navigator.share && card.cardUrl);
    return XBTN + heroHtml('<h2 class="cm-mc-title">Gift it to <em>a friend</em></h2>', true)
      + '<div class="cm-mc-body">' + cardHtml(card, 'To give')
      +   '<p class="cm-mc-on"><b>Send her the link.</b> Whoever opens it gets the card on their cart, and it comes off at their checkout. It&rsquo;s off your cart now.</p>'
      +   (canShare ? '<button type="button" class="cm-mc-btn cm-mc-coral" id="cm-mc-share">Send the link</button>'
                    : '<button type="button" class="cm-mc-btn cm-mc-coral" id="cm-mc-copylink">Copy the link</button>')
      +   '<div class="cm-mc-row">'
      +     (canShare ? '<button type="button" class="cm-mc-btn cm-mc-ghost" id="cm-mc-copylink">Copy the link</button>' : '')
      +     '<button type="button" class="cm-mc-btn cm-mc-ghost" id="cm-mc-keep">Keep it for me</button>'
      +   '</div>'
      +   fineHtml(card)
      + '</div>';
  }
  function pausedHtml() {
    return XBTN + heroHtml('<h2 class="cm-mc-title">Welcome to <em>Mum Club</em></h2>', true)
      + '<div class="cm-mc-body">'
      +   '<p class="cm-mc-on"><b>You&rsquo;re in.</b> Our gift card offer has finished for now, so there&rsquo;s no card this time.</p>'
      +   '<button type="button" class="cm-mc-btn cm-mc-coral" id="cm-mc-shop" style="margin-bottom:14px;">Keep shopping</button>'
      + '</div>';
  }
  function mcShow(html) {
    var box = modalEl && modalEl.querySelector('.cm-mc');
    if (!box) return;
    box.innerHTML = html;
    try { modalEl.scrollTop = 0; } catch (e) {}
  }

  function openModal(step2, surface) {
    if (modalEl) return;
    surface = surface || 'popup';
    // The card for joining is only for someone holding none of our gift cards, and only while it is on.
    if (!step2 && noOffer()) return;
    modalEl = document.createElement('div');
    if (step2) {
      modalEl.className = 'cm-modal-ov';
      modalEl.innerHTML = '<div class="cm-modal"><button type="button" class="cm-x" aria-label="Close">&times;</button>' + step2Html() + '</div>';
    } else {
      modalEl.className = 'cm-mc-ov';
      modalEl.innerHTML = '<div class="cm-mc" role="dialog" aria-modal="true" aria-label="Join Mum Club">' + joinHtml(surface) + '</div>';
    }
    document.body.appendChild(modalEl);
    // ONE POPUP PER PAGE (Mark, 2026-10-10: "If one popup shows then dont show the other one"). Said out
    // loud so the product page's take box (emamaco-packprice.liquid) keeps its gift card sheet away.
    if (!window.__cmPopupShown) window.__cmPopupShown = 'signup';
    try { window.dispatchEvent(new CustomEvent('cm:popup-shown', { detail: { by: 'signup' } })); } catch (e) {}
    if (!step2) cvr('section_viewed', { section: 'cm-signup', surface: surface, variant: 'mumclub-100' });
    modalEl.addEventListener('click', function (e) {
      var t = e.target;
      if (t === modalEl || (t && t.closest && t.closest('.cm-x, .cm-mc-x'))) closeModal(!step2);
    });
  }

  // ── her card: on her cart, and in this browser for the site's "your card" bar ───────────────────
  var mcCard = null;
  function stash(card) {
    var code = String(card.code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    try {
      localStorage.setItem('cm_ad_card', JSON.stringify({ g: card.token, last4: card.last4, amount_cents: card.amount_cents, expires_on: card.expires_on, code: code }));
      sessionStorage.removeItem('cm_ad_card_checked');
    } catch (e) {}
    return fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { emamaco_gift_card: code } })
    }).then(function (r) {
      // The take box re-points without its offer, and this script drops the tab.
      if (r.ok) { try { window.dispatchEvent(new CustomEvent('emamaco:gift-card-on')); } catch (e) {} }
      return r.ok;
    }).catch(function () { return false; });
  }
  function unstash() {
    CARD_ON = false;
    try { localStorage.removeItem('cm_ad_card'); sessionStorage.removeItem('cm_ad_card_checked'); } catch (e) {}
    return fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { emamaco_gift_card: '' } })
    }).catch(function () {});
  }
  function copyText(text, btn) {
    var label = btn ? btn.textContent : '';
    function ok() { if (btn) { btn.textContent = 'Copied'; setTimeout(function () { btn.textContent = label; }, 1800); } }
    function legacy() {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); ok(); } catch (e) {}
      document.body.removeChild(ta);
    }
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(ok, legacy); else legacy();
  }
  function attribution() {
    var a = {}, sp = new URLSearchParams(location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'ttclid'].forEach(function (k) { var v = sp.get(k); if (v) a[k] = String(v).slice(0, 200); });
    a.landing = location.pathname;
    try { if (document.referrer) a.referrer = new URL(document.referrer).hostname; } catch (e) {}
    return a;
  }
  function mcErr(msg) {
    var el = document.getElementById('cm-mc-err');
    if (el) { el.textContent = msg; el.style.display = msg ? 'block' : 'none'; }
  }
  var mcBusy = false;
  function joinGo(btn) {
    if (mcBusy) return;
    var input = document.getElementById('cm-mc-email');
    var email = String(input && input.value || '').trim();
    mcErr('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { mcErr('Check your email address and try again.'); if (input) input.focus(); return; }
    var surface = btn.getAttribute('data-surface') || 'popup';
    mcBusy = true; btn.disabled = true;
    btn.innerHTML = '<span class="cm-mc-wait" aria-hidden="true"></span>Making your card...';
    // Both at once: joining (her email and consent, the store's own signup) and her card.
    var banked = bank(email, surface).catch(function () { return false; });
    var claimed = fetch(CLAIM, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaign: 'mumclub', email: email, consent: true, attribution: attribution(), company: '' })
    }).then(function (r) { return r.json().catch(function () { return {}; }); })
      .catch(function () { return { ok: false, error: 'network' }; });
    Promise.all([banked, claimed]).then(function (res) {
      var j = res[1] || {};
      if (j.ok) {
        var card = { token: j.token, code: j.code, last4: j.last4, amount_cents: j.amountCents, expires_on: j.expiresOn, cardUrl: j.cardUrl, emailed: !!j.emailed };
        mcCard = card;
        lsSet(LS_JOINED, '1');
        cvr('interaction', { what: 'mumclub_card', surface: surface });
        return stash(card).then(function (onCart) { card.onCart = onCart; mcBusy = false; mcShow(joinedHtml(card)); });
      }
      mcBusy = false;
      cvr('interaction', { what: 'mumclub_card_failed', error: String(j.error || 'unknown').slice(0, 40) });
      // The offer is switched off, but she has joined: say so rather than show an error.
      if (j.error === 'paused' && res[0]) { lsSet(LS_JOINED, '1'); removeTab(); normalizeBadges(); mcShow(pausedHtml()); return; }
      btn.disabled = false; btn.textContent = 'Join and get my ' + amtText();
      mcErr(j.error === 'bad_email' ? 'Check your email address and try again.'
        : j.error === 'rate_limited' ? 'Too many tries from this connection. Please wait a few minutes and try again.'
        : j.error === 'paused' ? 'Our gift card offer has finished for now.'
        : j.error === 'network' ? 'We could not reach our server. Check your connection and try again.'
        : 'Something went wrong making your card. Please try again.');
    });
  }
  // Enter in an email field submits (there is no <form>, so without this the
  // keypress silently does nothing — the classic "typed my email, nothing happened"). Escape closes the popup.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modalEl) { closeModal(modalEl.className === 'cm-mc-ov'); return; }
    if (e.key !== 'Enter' || !e.target) return;
    if (e.target.id === 'cm-mc-email') { e.preventDefault(); var g = document.getElementById('cm-mc-go'); if (g) g.click(); }
    if (e.target.id === 'cm-sig-email') { e.preventDefault(); var a = document.getElementById('cm-sig-apply'); if (a) a.click(); }
  });
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t) return;
    // The Mum Club popup's buttons (their own ids, so a tap on the label or the spinner inside still counts).
    var mc = t.closest ? t.closest('#cm-mc-go, #cm-mc-no, #cm-mc-shop, #cm-mc-copy, #cm-mc-gift, #cm-mc-share, #cm-mc-copylink, #cm-mc-keep') : null;
    if (mc) {
      if (mc.id === 'cm-mc-go') { joinGo(mc); return; }
      if (mc.id === 'cm-mc-no') { closeModal(true); return; }
      if (mc.id === 'cm-mc-shop') { closeModal(false); return; }
      if (!mcCard) return;
      if (mc.id === 'cm-mc-copy') { copyText(fmtCode(mcCard.code), mc); cvr('interaction', { what: 'mumclub_card_copy' }); return; }
      if (mc.id === 'cm-mc-gift') {
        cvr('interaction', { what: 'mumclub_card_gift' });
        unstash().then(function () { mcCard.onCart = false; mcShow(giftHtml(mcCard)); });
        return;
      }
      if (mc.id === 'cm-mc-share') {
        try {
          navigator.share({ title: 'A ' + money(mcCard.amount_cents) + ' emamaco gift card', text: 'A ' + money(mcCard.amount_cents) + ' emamaco gift card for you. Open the link and it goes on your cart.', url: mcCard.cardUrl })
            .catch(function () {});
        } catch (err) {}
        return;
      }
      if (mc.id === 'cm-mc-copylink') { copyText(mcCard.cardUrl || fmtCode(mcCard.code), mc); return; }
      if (mc.id === 'cm-mc-keep') { stash(mcCard).then(function (onCart) { mcCard.onCart = onCart; mcShow(joinedHtml(mcCard)); }); return; }
    }
    // Stage chooser (expecting / baby-here): toggle the sub-panel + selected state.
    var seg = t.closest ? t.closest('.cm-seg-btn') : null;
    if (seg) {
      var all = seg.parentNode.querySelectorAll('.cm-seg-btn');
      for (var i = 0; i < all.length; i++) all[i].classList.remove('selected');
      seg.classList.add('selected');
      var exp = document.getElementById('cm-stage-expecting'), born = document.getElementById('cm-stage-born');
      if (exp) exp.style.display = seg.getAttribute('data-stage') === 'expecting' ? 'block' : 'none';
      if (born) born.style.display = seg.getAttribute('data-stage') === 'born' ? 'block' : 'none';
      return;
    }
    var ageBtn = t.closest ? t.closest('.cm-age-btn') : null;
    if (ageBtn) {
      var ages = ageBtn.parentNode.querySelectorAll('.cm-age-btn');
      for (var k = 0; k < ages.length; k++) ages[k].classList.remove('selected');
      ageBtn.classList.add('selected');
      return;
    }
    if (t.id === 'cm-m-skip') closeModal(false);
    if (t.id === 'cm-m-save') {
      var email2 = window.__cmSigEmail || '';
      if (!email2) { closeModal(false); return; }
      var name = (document.getElementById('cm-m-name') || {}).value || '';
      var stageSel = document.querySelector('.cm-seg-btn.selected');
      var stage = stageSel ? stageSel.getAttribute('data-stage') : '';
      var due = stage === 'expecting' ? ((document.getElementById('cm-m-due') || {}).value || '') : '';
      var ageSel = stage === 'born' ? document.querySelector('.cm-age-btn.selected') : null;
      var babyAge = ageSel ? ageSel.getAttribute('data-age') : '';
      var phone = (document.getElementById('cm-m-phone') || {}).value || '';
      var sms = !!((document.getElementById('cm-m-sms') || {}).checked);
      t.disabled = true; t.textContent = '...';
      submit({ email: email2, consentEmail: true, firstName: name, dueDate: due, babyAge: babyAge, phone: phone, consentSms: sms, surface: 'details', consentText: sms ? (CONSENT_TEXT_SMS + ' — ' + SMS_DISCLAIMER) : CONSENT_TEXT_MODAL })
        .then(function () {
          var box = modalEl && modalEl.querySelector('.cm-modal');
          if (box) box.innerHTML = '<button type="button" class="cm-x" aria-label="Close">&times;</button><h3>All set.</h3><p>Mum Club pricing is on your order and your picks just got smarter.</p>';
          setTimeout(function () { closeModal(false); }, 2200);
        })
        .catch(function () { closeModal(false); });
    }
  });

  // ── 3. sticky tab ──────────────────────────────────────────────────────────
  var tabEl = null;
  function removeTab() { if (tabEl) { tabEl.remove(); tabEl = null; } }
  function cartOrCheckout() { return /(^|\/)(cart|checkouts?)(\/|$)/.test(location.pathname); }
  // Not held back by the popup's snooze: after closing the popup she can still find the card here.
  function mountTab() {
    if (tabEl || noOffer() || cartOrCheckout()) return;
    tabEl = document.createElement('div');
    tabEl.className = 'cm-tab';
    tabEl.setAttribute('role', 'button');
    tabEl.setAttribute('tabindex', '0');
    // The X is INSIDE the bar, spaced before the label — its own easy tap target
    // (hides it on this page only); anywhere else on the bar opens the modal.
    tabEl.innerHTML = '<button type="button" class="cm-tab-x" aria-label="Dismiss">&times;</button>'
      + '<span class="cm-tab-label">' + amtText() + ' gift card</span>';
    tabEl.addEventListener('click', function (e) {
      if (e.target && e.target.classList.contains('cm-tab-x')) {
        removeTab();
        return;
      }
      openModal(false, 'sticky-tab');
    });
    document.body.appendChild(tabEl);
  }

  // ── popup triggers: any page, 15s after load OR exit-intent ───────────────
  var popupFired = false;
  function takesEngaged() {
    if (window.__cmTakesEngaged) return true;
    try { return sessionStorage.getItem('cm_takes_engaged') === '1'; } catch (e) { return false; }
  }
  function tryPopup() {
    if (popupFired || snoozed() || modalEl) return;
    if (noOffer()) { popupFired = true; return; }
    // Her take is being recorded or posted: not now. Tapping the box also marks the visit engaged (below), so
    // this is the guard for the screens themselves, whatever opened them.
    if (window.__cmTakesOpen) return;
    // The take box's gift card sheet already showed on this page, or she has tapped the take box this
    // visit (emamaco-packprice.liquid sets both): no popup (the sticky tab still opens it on a tap).
    if (window.__cmPopupShown === 'takes' || takesEngaged()) { popupFired = true; return; }
    popupFired = true;
    openModal(false, 'popup');
  }
  function boot() {
    // The tab waits for the cart: no flash of "$100 gift card" for someone whose card is already on.
    cardChecked.then(mountTab);
    // A product swap on the home page is a new page to her: a tab she closed comes back.
    window.addEventListener('em:v2:swapped', function () { cardChecked.then(mountTab); });
    normalizeBadges();
    setTimeout(tryPopup, 15000);
    document.addEventListener('mouseout', function (e) {
      if (!e.relatedTarget && e.clientY <= 0) tryPopup(); // desktop exit-intent
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // active(): not signed up at all (the cart's Mum Club box). offerActive(): the card offer is still hers to take.
  window.__cmSignup = { inlineHtml: inlineHtml, active: active, offerActive: function () { return !noOffer(); }, open: function (s) { openModal(false, s || 'manual'); } };
})();

